import axios, { AxiosRequestConfig } from "axios";
import {
  DocumentType,
  StatementStatus,
  Channel,
  CnpForm,
  HttpSuccessResponse,
  StatementResponse,
  StatementHTTPResponse,
  MerchantHttpSuccessResponse,
  InvoiceUpdateHttpSuccessResponse,
  StatementPaylinkHTTPSuccessResponse,
  GetStockAvailabilityResponse,
  Platform,
  RequestType,
} from "../../types";
import getBaseStatement from "./util";
import { BaseStatement, ResponseCode } from "./types";
import { HttpErrorResponse } from "../types";
import { generateHttpError } from "../util";
import {
  getChannel,
  getIdToken,
  getOwnerCookieExpiry,
  setIdToken,
  // getOwnerUserName,
  // getRefreshToken,
} from "../../utils/Jwt";
// import CognitoService from "../cognito/CognitoService";

// const cognitoService = new CognitoService(
//   process.env.REACT_APP_COGNITO_USERPOOL_ID!,
//   process.env.REACT_APP_COGNITO_CLIENT_ID!,
//   getOwnerUserName()
// );

// cognitoService.setSession(getIdToken(), "", getRefreshToken());

// const delay = (duration: number) =>
//   // eslint-disable-next-line no-promise-executor-return
//   new Promise((resolve) => setTimeout(resolve, duration));

interface MobileTokenRefreshInterface {
  getRefreshedToken(): string;
}

declare let JavaScriptBridge: MobileTokenRefreshInterface;

const statementServiceClient = (withToken: boolean) => {
  let headers: { ["Content-Type"]: string; Authorization?: string } = {
    "Content-Type": "application/json",
  };

  if (withToken) {
    headers = {
      ...headers,
      Authorization: `Bearer ${getIdToken()}`,
    };
  }

  return axios.create({
    baseURL: `https://${process.env.REACT_APP_API_DOMAIN}/statement`,
    timeout: parseInt(process.env.REACT_APP_DEFAULT_HTTP_TIMEOUT || "10000", 10),
    headers,
  });
};

const statementServiceClientRequest = async <T extends any, D extends any>(
  addAuth: boolean,
  type: RequestType,
  url: string,
  body?: D,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  try {
    switch (type) {
      case RequestType.GET: {
        const { data } = await statementServiceClient(addAuth).get<T>(url);
        return data as T;
      }
      case RequestType.PUT: {
        const { data } = await statementServiceClient(addAuth).put(url, body, config);
        return data as T;
      }
      case RequestType.POST: {
        const { data } = await statementServiceClient(addAuth).post(url, body, config);
        return data as T;
      }
      default: {
        const { data } = await statementServiceClient(addAuth).get<T>(url);
        return data as T;
      }
    }
  } catch (e) {
    return generateHttpError(e) as T;
  }
};

// Delay function (delay is in milliseconds)
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const statementServiceClientRequestWithRetry = async <T extends any, D extends any>(
  addAuth: boolean,
  type: RequestType,
  url: string,
  body?: D,
  config?: AxiosRequestConfig<D>,
): Promise<T> => {
  let response: any = await statementServiceClientRequest<T, D>(addAuth, type, url, body, config);

  if (
    (getChannel() === Channel.ANDROID_TOP || getChannel() === Channel.IOS_TOP) &&
    response.success === false &&
    response.statusCode === 401
  ) {
    const channel = getChannel();

    if (channel === Channel.ANDROID_TOP || channel === Channel.IOS_TOP) {
      if (channel === Channel.ANDROID_TOP) {
        const updateIdToken = JavaScriptBridge.getRefreshedToken();
        setIdToken(updateIdToken);
      } else if (channel === Channel.IOS_TOP) {
        (window as any).webkit.messageHandlers.JavaScriptBridge.postMessage("");
      }
      // Delay for a short time to allow cookie to update (e.g., 3000 milliseconds)
      await delay(3000);
    }

    response = await statementServiceClientRequest<T, D>(addAuth, type, url, body, config);
  }

  return response;
};

export const getInvoices = async (
  owner: string,
  limit: number,
  forward: boolean,
  lastStatement: string,
): Promise<StatementHTTPResponse | HttpErrorResponse> => {
  const response = await statementServiceClientRequestWithRetry<StatementHTTPResponse | HttpErrorResponse, undefined>(
    true,
    RequestType.GET,
    `owner/${owner}/statements?lastStatement=${lastStatement}&limit=${limit}&forward=${forward}&documentType="INVOICE"&statuses=UNPAID,PAID`,
  );

  return response;
};

export const getMerchantDetails = async (owner: string): Promise<MerchantHttpSuccessResponse | HttpErrorResponse> => {
  const response = await statementServiceClientRequestWithRetry<MerchantHttpSuccessResponse | HttpErrorResponse, undefined>(
    true,
    RequestType.GET,
    `owner/${owner}/info`,
  );

  return response;
};

export const updateInvoice = async (owner: string, id: string, createDate: string, invoiceData: CnpForm): Promise<any> => {
  const response = await statementServiceClientRequestWithRetry<any, BaseStatement>(
    true,
    RequestType.PUT,
    `owner/${owner}/statement`,
    getBaseStatement(id, createDate, invoiceData),
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return response;
};

export const updateInvoiceToUnpaid = async (
  owner: string,
  id: string,
  createDate: string,
  invoiceData: CnpForm,
): Promise<InvoiceUpdateHttpSuccessResponse | HttpErrorResponse> => {
  const response = await statementServiceClientRequestWithRetry<
    InvoiceUpdateHttpSuccessResponse | HttpErrorResponse,
    BaseStatement
  >(true, RequestType.PUT, `owner/${owner}/statement`, getBaseStatement(id, createDate, invoiceData, StatementStatus.UNPAID));

  return response;
};

export const createInvoice = async (owner: string): Promise<any> => {
  const response = await statementServiceClientRequestWithRetry<
    any,
    { documentType: DocumentType; platform: Platform; channel?: string }
  >(
    true,
    RequestType.POST,
    `owner/${owner}/statement`,
    {
      documentType: DocumentType.INVOICE,
      platform: Platform.INVOICE_TOOL,
      channel: getChannel(),
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.statement;
};

export const emailCustomerStatement = async (owner: string, createDate: string, statementNumber: string): Promise<boolean> => {
  const response = await statementServiceClientRequestWithRetry<any, { statementNumber: string; createDate: string }>(
    true,
    RequestType.POST,
    `/owner/${owner}/sendstatement`,
    {
      statementNumber,
      createDate,
    },
  );

  if (response.code !== ResponseCode.IK_01) return false;
  return true;
};

export const getStatement = async (id: string, docType: DocumentType): Promise<HttpSuccessResponse | HttpErrorResponse> => {
  const response = await statementServiceClientRequestWithRetry<StatementResponse | HttpErrorResponse, undefined>(
    false,
    RequestType.GET,
    `/statement/${id}?documentType=${docType}`,
    undefined,
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if ("code" in response) {
    const statementResponse = response;
    return {
      statementResponse,
      success: true,
    };
  }

  return response;
};

export const getStatementPaylink = async (
  id: string,
  docType: DocumentType,
): Promise<StatementPaylinkHTTPSuccessResponse | HttpErrorResponse> => {
  const response = await statementServiceClientRequestWithRetry<
    StatementPaylinkHTTPSuccessResponse | HttpErrorResponse,
    undefined
  >(false, RequestType.GET, `/statement/${id}/paylink?documentType=${docType}`);

  return response;
};

export const getStockAvailability = async (
  owner: string,
  idList: string,
): Promise<GetStockAvailabilityResponse | HttpErrorResponse> => {
  try {
    const url = `product-management/v1/owners/${owner}/catalogue/stock/availability/${idList}`;
    const response = await fetch(`https://${process.env.REACT_APP_API_DOMAIN}/${url}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const data = await response.json();
    if (!response.ok) {
      return generateHttpError({
        response: {
          status: response.status,
          data,
        },
      });
    }
    return data as GetStockAvailabilityResponse;
  } catch (e) {
    return generateHttpError(e);
  }
};
